import { TEMPLATE_REVIEW_STATUSES, UserRole } from '@root/@types/types';
import { getFeatureFlag } from '@root/helpers/feature-flags';

export type TemplateTransitionStatus = (typeof TEMPLATE_REVIEW_STATUSES)[number];

type TemplateTransitionsByStatus = Record<TemplateTransitionStatus, TemplateTransitionStatus[]>;

const STANDARD_TEMPLATE_TRANSITIONS: TemplateTransitionsByStatus = {
  ARCHIVED: ['DRAFT'],
  DRAFT: ['ARCHIVED', 'PUBLISHED'],
  PUBLISHED: ['DRAFT', 'ARCHIVED'],
  REVIEW: [],
};

const TEMPLATE_REVIEW_ADMIN_TRANSITIONS: TemplateTransitionsByStatus = {
  ARCHIVED: ['DRAFT'],
  DRAFT: ['REVIEW', 'ARCHIVED'],
  PUBLISHED: [],
  REVIEW: ['DRAFT', 'ARCHIVED'],
};

const TEMPLATE_REVIEW_SUPERADMIN_TRANSITIONS: TemplateTransitionsByStatus = {
  ARCHIVED: ['DRAFT'],
  DRAFT: ['REVIEW', 'ARCHIVED'],
  PUBLISHED: ['DRAFT', 'ARCHIVED'],
  REVIEW: ['PUBLISHED', 'DRAFT', 'ARCHIVED'],
};

const isTemplateTransitionStatus = (status?: string): status is TemplateTransitionStatus =>
  TEMPLATE_REVIEW_STATUSES.includes(status as TemplateTransitionStatus);

export const getAllowedTemplateTargetStatuses = ({
  sourceStatus,
  isTemplateReviewWorkflowEnabled,
  userRole,
}: {
  sourceStatus?: string;
  isTemplateReviewWorkflowEnabled: boolean;
  userRole?: UserRole | string;
}): TemplateTransitionStatus[] => {
  if (!isTemplateTransitionStatus(sourceStatus)) {
    return [];
  }

  const transitions = isTemplateReviewWorkflowEnabled
    ? userRole === UserRole.Superadmin
      ? TEMPLATE_REVIEW_SUPERADMIN_TRANSITIONS
      : TEMPLATE_REVIEW_ADMIN_TRANSITIONS
    : STANDARD_TEMPLATE_TRANSITIONS;

  return transitions[sourceStatus];
};

export const canTransitionTemplateStatus = ({
  sourceStatus,
  targetStatus,
  userRole,
}: {
  sourceStatus?: string;
  targetStatus?: string;
  userRole?: UserRole | string;
}) => {
  if (!isTemplateTransitionStatus(targetStatus)) {
    return false;
  }

  const isTemplateReviewWorkflowEnabled = getFeatureFlag('TemplateReviewWorkflow');

  if (!isTemplateReviewWorkflowEnabled && targetStatus === 'REVIEW') {
    return false;
  }

  if (sourceStatus === targetStatus) {
    return true;
  }

  return getAllowedTemplateTargetStatuses({
    sourceStatus,
    isTemplateReviewWorkflowEnabled,
    userRole,
  }).includes(targetStatus);
};
